// components/ui/BrandMark.tsx
// The enosys mark: a square plate with a square hole, drawn in oblique
// projection, with one corner cube lifted out and floating up-and-right.
//
// GEOMETRY — recovered from the source artwork (frontend/public/favicon.png)
// by fitting this model to it. Rasterising the polygons below and comparing
// tone-per-pixel against the original agrees on all 50,300 comparable pixels
// (outline and anti-aliased edge pixels excluded — the flat mark has no
// outline to match). Re-runnable: frontend/scripts/verify_brand_mark.py, which
// parses its coordinates out of THIS file so it fails if these drift.
//
// Read that as "the construction and the recession are right", not "every
// vertex is verified to the pixel": the artwork's outline covers each edge, so
// a single edge nudged a few px hides inside it. The depth vector is caught
// (1px off => 55 mismatches) because it moves nine polygons coherently. The
// script's docstring has the measured sensitivity table.
//
// Every coordinate follows from a single parameter, the arm width `a = 42`:
//
//   plate front face   152 x 152      (= a + hole + a = 42 + 68 + 42)
//   hole               68 x 68        centred, so each arm is `a` wide
//   notch              a x a          removed from the top-right corner
//   floating cube      a x a          the removed corner itself
//   depth vector d     (+28, -19)     oblique recession, up-and-right
//
// The notch's bottom-left corner and the hole's top-right corner are the SAME
// point (110, 74) — the cut is exactly one arm width, so it lands on the hole.
// That coincidence is what makes the mark read as one construction rather than
// a square with a bite taken out, and it is why the front face is drawn as a
// single evenodd path instead of separate rectangles.
//
// Three faces are visible, and each surface takes its tone from which way it
// points — front (toward viewer) light, top (up) mid, right (+x) dark. That
// rule also governs the two walls seen through the hole: the hole recedes the
// same direction as the plate, so looking in you see its LEFT wall (points +x,
// dark) and its BOTTOM wall (points up, mid). Getting those two from the same
// rule as the outer faces is what keeps the solid reading as solid.
//
// Draw order matters and is load-bearing:
//   1. outer 3D faces        2. hole walls (they overhang the opening)
//   3. front face            — its evenodd hole clips the walls to the opening
//   4. floating cube         — it genuinely occludes the notch floor
// The walls are NOT pre-clipped; the front face does that work. Reordering
// these will leak wall geometry across the plate.

import type { SVGProps } from 'react';
import { cn } from './cn';

export type BrandMarkVariant = 'institution' | 'enosys';

export interface BrandMarkProps
  extends Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'width' | 'height'> {
  /**
   * Rendered height in px. Width is computed from it at the artwork's true
   * 216:184 ratio — see WIDTH_RATIO for why it is computed rather than `auto`.
   * Defaults to the header lockup size.
   */
  size?: number;
  /**
   * `institution` (default) tints the mark with the active school's brand
   * ramp. `enosys` uses the company teal — for pre-auth surfaces, where no
   * institution is resolved and the mark is signing enosys' name, not a
   * school's.
   */
  variant?: BrandMarkVariant;
  /**
   * Accessible name. Omit for decorative use (the mark is then hidden from
   * assistive tech, which is correct when adjacent text already names it).
   */
  title?: string;
}

// Face polygons in viewBox units. Named for the surface, not the tone, so the
// lighting rule above stays legible at the call site.
const PLATE_TOP = '0,32 110,32 138,13 28,13';
const NOTCH_FLOOR = '110,74 152,74 180,55 138,55';
const NOTCH_WALL = '110,32 138,13 138,55 110,74';
const PLATE_SIDE = '152,74 180,55 180,165 152,184';
const HOLE_LEFT_WALL = '42,74 70,55 70,123 42,142';
const HOLE_BOTTOM_WALL = '42,142 110,142 138,123 70,123';
const CUBE_TOP = '146,19 188,19 216,0 174,0';
const CUBE_SIDE = '188,19 216,0 216,42 188,61';
const CUBE_FRONT = '146,19 188,19 188,61 146,61';

// Outer boundary (with the corner notch) followed by the hole. evenodd turns
// the second subpath into a true cutout, so the plate is see-through where the
// hole is not backed by a wall — the mark keeps a real hole on any background.
const PLATE_FRONT = 'M0,32 H110 V74 H152 V184 H0 Z M42,74 H110 V142 H42 Z';

// The artwork's true bounding box is 216x184 — not square. Width is derived
// from height rather than left to `width="auto"` on purpose: `auto` is an SVG2
// geometry-property value, and a browser that doesn't honour it falls back to
// the SVG 1.1 lacuna value of 100%, which would stretch the mark to its
// container instead of failing visibly. Computing the number removes the
// question rather than betting on spec support.
const WIDTH_RATIO = 216 / 184;
const round2 = (n: number) => Math.round(n * 100) / 100;

const BrandMark = ({
  size = 28,
  variant = 'institution',
  title,
  className,
  ...props
}: BrandMarkProps) => {
  const labelled = Boolean(title);

  return (
    <svg
      viewBox="0 0 216 184"
      height={size}
      width={round2(size * WIDTH_RATIO)}
      className={cn(
        'brand-mark',
        variant === 'enosys' && 'brand-mark--enosys',
        className,
      )}
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      focusable="false"
      {...props}
    >
      {labelled && <title>{title}</title>}

      {/* 1. outer faces */}
      <polygon points={PLATE_TOP} className="brand-mark__top" />
      <polygon points={NOTCH_FLOOR} className="brand-mark__top" />
      <polygon points={NOTCH_WALL} className="brand-mark__side" />
      <polygon points={PLATE_SIDE} className="brand-mark__side" />

      {/* 2. hole walls — overhang the opening; clipped by the front face below */}
      <polygon points={HOLE_LEFT_WALL} className="brand-mark__side" />
      <polygon points={HOLE_BOTTOM_WALL} className="brand-mark__top" />

      {/* 3. front face — its evenodd cutout is what clips the walls */}
      <path d={PLATE_FRONT} fillRule="evenodd" className="brand-mark__front" />

      {/* 4. the lifted corner, drawn last so it occludes the notch floor */}
      <polygon points={CUBE_TOP} className="brand-mark__top" />
      <polygon points={CUBE_SIDE} className="brand-mark__side" />
      <polygon points={CUBE_FRONT} className="brand-mark__front" />
    </svg>
  );
};

export default BrandMark;
